import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { action, computed, makeObservable, observable } from 'mobx';

import { postRequest } from '../../services/api';
import { devError } from '../../utils/log';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import { getDefaultTocTrophy, DEFAULT_BETWEEN_ROUND_TROPHY, DEFAULT_STARTS_IN_TROPHY } from './constants';
import { shouldShowEliminationSummary } from './tocAdvancement';
import { resolveTocMock, TOC_MOCK_ACKNOWLEDGED } from './tocMockFlags';
import { findPendingEndOfRoundRoundIndex, isTocRoundPendingAcknowledgement } from './tocPendingRound';
import {
  type Tournament,
  type TournamentOfChampions,
  TournamentOfChampionsState,
  type TournamentV2Response,
} from './types';

export class TournamentOfChampionsStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public tournamentOfChampions: TournamentOfChampions | null = null;

  @observable
  public endOfRoundDismissedForRound: number | null = null;

  @observable
  public eliminationComplete = false;

  @computed
  public get state(): TournamentOfChampionsState | null {
    return this.tournamentOfChampions?.state ?? null;
  }

  @computed
  public get isPreStart(): boolean {
    return this.tournamentOfChampions?.state === TournamentOfChampionsState.PRE_CHAMPIONS;
  }

  @computed
  public get isActiveRound(): boolean {
    return this.tournamentOfChampions?.state === TournamentOfChampionsState.ACTIVE_ROUND;
  }

  @computed
  public get currentRound() {
    if (!this.tournamentOfChampions) return null;
    return (
      this.tournamentOfChampions.rounds.find((r) => r.roundIndex === this.tournamentOfChampions?.currentRoundIndex) ??
      null
    );
  }

  @computed
  public get nextRound() {
    return this.tournamentOfChampions?.rounds.find((r) => !r.isFinalized) ?? null;
  }

  @computed
  public get isBetweenRounds(): boolean {
    return (
      this.tournamentOfChampions?.state === TournamentOfChampionsState.BETWEEN_ROUNDS_QUALIFIED ||
      this.tournamentOfChampions?.state === TournamentOfChampionsState.BETWEEN_ROUNDS_ELIMINATED
    );
  }

  @computed
  public get isChampionsCompleted(): boolean {
    return this.tournamentOfChampions?.state === TournamentOfChampionsState.CHAMPIONS_COMPLETED;
  }

  @computed
  public get isSummaryWidget(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc || this.eliminationComplete) return false;
    return shouldShowEliminationSummary(toc);
  }

  @computed
  public get shouldShowWidget(): boolean {
    if (!this.tournamentOfChampions) return false;
    if (this.eliminationComplete) return false;
    if (this.isChampionsCompleted) return false;
    if (this.hasPendingPopup) return true;
    return this.isPreStart || this.isActiveRound || this.isBetweenRounds;
  }

  @computed
  public get shouldRenderDialog(): boolean {
    return !!this.tournamentOfChampions && !this.eliminationComplete;
  }

  @computed
  public get isEliminated(): boolean {
    return this.tournamentOfChampions?.isEliminated === true;
  }

  @computed
  public get hasPendingRoundAcknowledgement(): boolean {
    return this.tournamentOfChampions?.rounds.some((r) => isTocRoundPendingAcknowledgement(r)) ?? false;
  }

  @computed
  public get canOpenBetweenRoundsInfo(): boolean {
    return this.isBetweenRounds && !this.isEliminated;
  }

  @computed
  public get canOpenDialog(): boolean {
    return (
      this.isPreStart ||
      this.isActiveRound ||
      this.hasPendingRoundAcknowledgement ||
      this.canOpenBetweenRoundsInfo ||
      this.hasPendingPopup ||
      this.isChampionsCompleted
    );
  }

  @computed
  public get nextRoundStartDate(): string | null {
    return this.nextRound?.startDate ?? null;
  }

  @computed
  public get firstRoundStartDate(): string | null {
    return this.tournamentOfChampions?.rounds[0]?.startDate ?? null;
  }

  @computed
  public get isInCountdownState(): boolean {
    return this.isPreStart || (this.isBetweenRounds && !this.isEliminated);
  }

  private get rawCountdownEndDate(): string | null {
    if (!this.isInCountdownState) return null;
    if (this.isPreStart) return this.firstRoundStartDate;
    return this.nextRoundStartDate;
  }

  private isDateInPast(iso: string | null | undefined): boolean {
    if (!iso) return false;
    const parsed = dayjs(iso);
    if (!parsed.isValid()) return false;
    return parsed.isBefore(dayjs());
  }

  @computed
  public get countdownEndDate(): string | null {
    const raw = this.rawCountdownEndDate;
    if (this.isDateInPast(raw)) return null;
    return raw;
  }

  @computed
  public get isCountdownToNextRound(): boolean {
    return this.countdownEndDate != null;
  }

  @computed
  public get isOnFinalRound(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc) return false;
    return this.isActiveRound && toc.currentRoundIndex === toc.rounds.length - 1;
  }

  private get hasUndismissedEndOfRoundClaim(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc) return false;
    const pendingEndOfRoundIndex = findPendingEndOfRoundRoundIndex(toc.rounds);
    return pendingEndOfRoundIndex >= 0 && this.endOfRoundDismissedForRound !== pendingEndOfRoundIndex;
  }

  private get hasUndismissedEliminationSummary(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc) return false;
    return toc.isEliminated === true && shouldShowEliminationSummary(toc) && !this.eliminationComplete;
  }

  @computed
  public get hasPendingPopup(): boolean {
    return this.hasUndismissedEndOfRoundClaim || this.hasUndismissedEliminationSummary;
  }

  @computed
  public get widgetIcon(): string | null {
    if (!this.tournamentOfChampions) return null;
    if (this.eliminationComplete) return null;

    if (this.isInCountdownState) {
      return DEFAULT_STARTS_IN_TROPHY;
    }

    if (this.isActiveRound) {
      const roundIndex = this.tournamentOfChampions.currentRoundIndex;
      return (
        this.currentRound?.cupActiveImg ?? getDefaultTocTrophy(roundIndex, this.tournamentOfChampions.rounds.length)
      );
    }

    if (this.isBetweenRounds || this.isSummaryWidget) {
      const nextRound = this.nextRound;
      return (
        nextRound?.cupActiveImg ??
        this.tournamentOfChampions.assets.betweenRoundsWidgetImg ??
        DEFAULT_BETWEEN_ROUND_TROPHY
      );
    }

    return null;
  }

  @action
  public setTocData(tournamentOfChampions: TournamentOfChampions | null) {
    const isSameChampions =
      !!tournamentOfChampions && tournamentOfChampions.championsKey === this.tournamentOfChampions?.championsKey;

    if (!tournamentOfChampions || !isSameChampions) {
      this.endOfRoundDismissedForRound = null;
      this.eliminationComplete = false;
    }

    this.tournamentOfChampions = tournamentOfChampions;

    if (tournamentOfChampions && tournamentOfChampions.rounds.length > 0) {
      const pendingEndOfRoundIndex = findPendingEndOfRoundRoundIndex(tournamentOfChampions.rounds);
      const hasUnacknowledgedParticipation = pendingEndOfRoundIndex >= 0;
      if (hasUnacknowledgedParticipation) {
        if (this.endOfRoundDismissedForRound === pendingEndOfRoundIndex) {
          this.endOfRoundDismissedForRound = null;
        }
      }
    }

    if (resolveTocMock() && TOC_MOCK_ACKNOWLEDGED) {
      if (TOC_MOCK_ACKNOWLEDGED.dismissedRound !== undefined) {
        this.endOfRoundDismissedForRound = TOC_MOCK_ACKNOWLEDGED.dismissedRound;
      }
      if (TOC_MOCK_ACKNOWLEDGED.eliminationComplete) {
        this.eliminationComplete = true;
      }
    }

    if (
      tournamentOfChampions &&
      tournamentOfChampions.state === TournamentOfChampionsState.CHAMPIONS_COMPLETED &&
      !tournamentOfChampions.isEliminated
    ) {
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['PostTournament']);
    }

    this.autoOpenPendingPopup();
  }

  @action
  public reset() {
    this.tournamentOfChampions = null;
    this.endOfRoundDismissedForRound = null;
    this.eliminationComplete = false;
  }

  @action
  public dismissEndOfRound(roundIndex: number) {
    this.endOfRoundDismissedForRound = roundIndex;
  }

  @action
  public completeElimination() {
    this.eliminationComplete = true;
  }

  public async finishChampionsForPlayer(championsKey: string) {
    if (!championsKey) return;
    try {
      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/champions/dismiss-summary/${championsKey}/${coinType}`);
    } catch (e) {
      devError('finishChampionsForPlayer failed', e);
    }

    this.rootStore.featureManagerStore.onDialogFinished('PostTournament');

    try {
      await this.rootStore.tournamentStore.fetch();
    } catch (e) {
      devError('finishChampionsForPlayer refetch failed', e);
    }
  }

  public getMockFetchPayload(): TournamentV2Response['tournaments'] | null {
    const tocMock = resolveTocMock();
    if (!tocMock) return null;
    const fresh = tocMock().tournaments;
    const live = this.tournamentOfChampions;
    if (!live || !fresh.tournamentOfChampions || live.championsKey !== fresh.tournamentOfChampions.championsKey) {
      return fresh;
    }

    const mergedRounds = fresh.tournamentOfChampions.rounds.map((round, index) => ({
      ...round,
      roundAcknowledged: live.rounds[index]?.roundAcknowledged ?? round.roundAcknowledged,
    }));

    return {
      ...fresh,
      tournamentOfChampions: {
        ...fresh.tournamentOfChampions,
        rounds: mergedRounds,
      },
    };
  }

  public normalizeV2TournamentsPayload(data: TournamentV2Response['tournaments']): TournamentV2Response['tournaments'] {
    if (!data.tournamentOfChampions) return data;
    return {
      ...data,
      tournamentOfChampions: this.normalize(data.tournamentOfChampions),
    };
  }

  @action
  public ingestFetchResult({
    currentTournament,
    previousTournament,
    tournamentOfChampions,
    coinType,
  }: {
    currentTournament: Tournament | null;
    previousTournament: Tournament | null;
    tournamentOfChampions: TournamentOfChampions | null;
    coinType: 'gc' | 'sc';
  }): Tournament | null | undefined {
    this.setTocData(tournamentOfChampions);

    if (currentTournament?.leaderboards[coinType]) {
      return currentTournament;
    }
    if (!tournamentOfChampions && previousTournament?.leaderboards[coinType]) {
      return previousTournament;
    }
    if (tournamentOfChampions) {
      return null;
    }
    return undefined;
  }

  public async finishRoundForPlayer(tournamentKey: string | undefined, options?: { deferFetch?: boolean }) {
    if (!tournamentKey) {
      devError('[TOC] No tournament key for round finish');
      return;
    }

    const loadingKey = 'finishTocRoundForPlayer';
    this.rootStore.tournamentStore.setLoading(loadingKey, true);
    try {
      if (resolveTocMock()) {
        this.acknowledgeMockRoundInPlace(tournamentKey);
        if (!options?.deferFetch) {
          await this.rootStore.tournamentStore.fetch(undefined, { force: true });
        }
        return;
      }

      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/finish/${tournamentKey}/${coinType}`);
      if (!options?.deferFetch) {
        await this.rootStore.tournamentStore.fetch(undefined, { force: true });
      }
    } finally {
      this.rootStore.tournamentStore.setLoading(loadingKey, false);
    }
  }

  public get isFetchReadyWithTocOnly(): boolean {
    return !!this.tournamentOfChampions;
  }

  @action
  private acknowledgeMockRoundInPlace(tournamentKey: string) {
    const rounds = this.tournamentOfChampions?.rounds;
    if (!rounds?.length) return;

    const round =
      rounds.find((r) => r.playerTournamentKey === tournamentKey) ??
      rounds.find((r) => r.isFinalized && r.roundAcknowledged !== true);
    if (!round) return;

    round.roundAcknowledged = true;
  }

  private autoOpenPendingPopup() {
    if (this.rootStore.uiStore.openDialog.dialog != null) return;
    const isGc = this.rootStore.coinTypeStore.coinType === 'GC';
    const shouldOpen = this.hasUndismissedEliminationSummary || (this.hasUndismissedEndOfRoundClaim && !isGc);
    if (!shouldOpen) return;

    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: { shown_by: 'log_in' },
    });
  }

  public normalize(raw: any): TournamentOfChampions {
    const normalizePrizePool = (pool: any) => {
      if (isNil(pool)) return null;
      const toNum = (v: unknown): number => {
        if (isNil(v)) return 0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      const hasGc = Object.prototype.hasOwnProperty.call(pool, 'gc');
      const hasSc = Object.prototype.hasOwnProperty.call(pool, 'sc');
      if (hasGc || hasSc) {
        return { gcCoin: toNum(pool.gc), scCoin: toNum(pool.sc) };
      }
      return { gcCoin: toNum(pool.gcCoin), scCoin: toNum(pool.scCoin) };
    };

    return {
      ...raw,
      assets: {
        bannerBackgroundImg: raw.assets?.bannerBackgroundImg ?? null,
        bannerAnimation: raw.assets?.bannerAnimation ?? null,
        summaryAnimation: raw.assets?.summaryAnimation ?? null,
        betweenRoundsWidgetImg: raw.assets?.betweenRoundsWidgetImg ?? null,
        introAnimation: raw.assets?.introAnimation ?? null,
        endOfRoundAnimation: raw.assets?.endOfRoundAnimation ?? null,
        betweenRoundsAnimation: raw.assets?.betweenRoundsAnimation ?? null,
        shareBackgroundImg: raw.assets?.shareBackgroundImg ?? null,
        qualificationLineQualifiedImg: raw.assets?.qualificationLineQualifiedImg ?? null,
        qualificationLineEliminatedImg: raw.assets?.qualificationLineEliminatedImg ?? null,
        leaderboardTrophyAnimation: raw.assets?.leaderboardTrophyAnimation ?? null,
      },

      rounds: raw.rounds.map((round: any) => ({
        ...round,
        cupActiveImg: round.cupActiveImg ?? null,
        cupInactiveImg: round.cupInactiveImg ?? null,
        widgetDesignImg: round.widgetDesignImg ?? null,
        playersToAdvanceLimit: round.playersToAdvanceLimit ?? null,
        prizePool: normalizePrizePool(round.prizePool),
      })),
    };
  }
}
